import React, { useEffect, useRef } from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import { homeOwnershipsApi } from 'prequalificationV2/api/HomeOwnershipsApi';
import { setLoanPortal } from 'prequalificationV2/actions/dashboardPortal';
import { setCoOwner } from 'prequalificationV2/actions/coOwnerInfo';
import { DocumentCategory } from 'enums/DocumentCategory';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import {
  getDocumentsByCategory,
  isDocumentCategoryCompleted,
  isDocumentCategoryRequired,
} from 'prequalificationV2/utils/documentsUtils';
import UploadDocumentsCategory from 'prequalificationV2/components/LoanPortal/UploadDocuments/UploadDocumentCategory';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { getErrorMessageForRequiredFields, removeNonDigits, removeSpaces } from 'prequalificationV2/utils/stringUtils';
import {
  getMessageForInvalidFields,
  isValidEmail,
  validatePhoneNumber,
} from 'prequalificationV2/utils/validationUtils';
import { LoadingType, setLoadingState } from 'prequalificationV2/actions';
import { DashboardTabName } from 'enums/DashboardTabName';
import { FailureNotificationType } from 'notifications';
import * as notificationManager from 'notifications';
import Input from 'components/Inputs/Input';
import Loader from 'components/Loader';
import Section from 'prequalificationV2/components/LoanPortal/Sections/Section';
import SubSection, { SubSectionState } from 'prequalificationV2/components/LoanPortal/Sections/SubSection/SubSection';
import { Button } from 'components/Buttons/Button';
import HomeOwnershipInfo from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/HomeOwnershipTab/HomeOwnershipInfo/HomeOwnershipInfo';
import { Lender } from 'enums/ApplicationsVariables';
import { LAST_4_DIGITS_SSN_LENGTH } from 'prequalificationV2/utils/constants';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput';

import styles from '../InformationTabs.module.scss';
import { CoOwnerInputLabel, CoOwnerInputName } from './HomeOwnershipTab.enums';

const HomeOwnershipTab = () => {
  const dispatch = useAppDispatch();

  const loading = useAppSelector(
    (state) => state.loading[`${LoadingType.RequiredInformationUpdating}${DashboardTabName.HomeOwnership}`],
  );
  const coOwnerState = useAppSelector((state) => state.coOwnerInfo);
  const application = useAppSelector((state) => state.application);
  const { email: borrowerEmail } = useAppSelector((state) => state.borrower);
  const { email: coBorrowerEmail } = useAppSelector((state) => state.coBorrower);
  const borrowerPhone = application.variables?.borrower_phone;
  const coBorrowerPhone = application.variables?.coborrower_phone;
  const { requiredDocuments } = useAppSelector((state) => state.documents);
  const { isThereNonBorrowingCoOwner, isCoOwnerInformationComplete, isHomeOwnershipInformationComplete } =
    useAppSelector((state) => state.dashboardPortal);
  const isQuiloLender = application.variables?.lender === Lender.Quilo;
  const coOwnerInformationSectionRolledUp = useRef(isCoOwnerInformationComplete);

  const { register, unregister, trigger, handleSubmit, watch, formState, setValue, clearErrors, ...methods } =
    useForm<any>({
      defaultValues: {
        [CoOwnerInputName.FirstName]: coOwnerState.firstName,
        [CoOwnerInputName.LastName]: coOwnerState.lastName,
        [CoOwnerInputName.MiddleName]: coOwnerState.middleName,
        [CoOwnerInputName.Address]: coOwnerState.address,
        [CoOwnerInputName.City]: coOwnerState.city,
        [CoOwnerInputName.State]: coOwnerState.state,
        [CoOwnerInputName.Zip]: coOwnerState.zip,
        [CoOwnerInputName.PhoneNumber]: coOwnerState.phoneNumber || '',
        [CoOwnerInputName.Email]: coOwnerState.email,
        [CoOwnerInputName.Last4Ssn]: coOwnerState.last4SSN || '',
      },
    });

  const watcher = watch();

  useEffect(() => {
    register(CoOwnerInputName.Email, {
      required: getErrorMessageForRequiredFields(CoOwnerInputLabel.Email),
      validate: {
        validEmail: (value) => isValidEmail(value) || 'Please enter a valid email address',
        notEqualBorrower: (value) => value !== borrowerEmail || 'Must be different from Borrower Email',
        notEqualCoBorrower: (value) => value !== coBorrowerEmail || 'Must be different from Co-Borrower Email',
      },
    });

    if (isQuiloLender) {
      register(CoOwnerInputName.PhoneNumber, {
        required: getErrorMessageForRequiredFields(CoOwnerInputLabel.PhoneNumber),
        validate: {
          validPhone: (value) => validatePhoneNumber(value),
          notEqualBorrower: (value) => value !== borrowerPhone || 'Must be different from Borrower Phone',
          notEqualCoBorrower: (value) => value !== coBorrowerPhone || 'Must be different from Co-Borrower Phone',
        },
      });
      register(CoOwnerInputName.Last4Ssn, {
        validate: (value) => {
          if (coOwnerState.last4SSN) {
            return true;
          }

          if (!value) {
            return getErrorMessageForRequiredFields(CoOwnerInputLabel.Last4Ssn);
          }

          return value.length === LAST_4_DIGITS_SSN_LENGTH || getMessageForInvalidFields(CoOwnerInputLabel.Last4Ssn);
        },
      });
    } else {
      unregister([CoOwnerInputName.PhoneNumber, CoOwnerInputName.Last4Ssn]);
    }
  }, [
    coOwnerState.last4SSN,
    isQuiloLender,
    register,
    unregister,
    borrowerPhone,
    coBorrowerPhone,
    borrowerEmail,
    coBorrowerEmail,
  ]);

  const onSubmit = handleSubmit(async () => {
    try {
      dispatch(
        setLoadingState({
          type: `${LoadingType.RequiredInformationUpdating}${DashboardTabName.HomeOwnership}`,
          loading: true,
        }),
      );

      await homeOwnershipsApi.updateCoOwner({
        applicationId: application.applicationId!,
        email: watcher[CoOwnerInputName.Email]!,
        lender: application.variables?.lender as Lender,
        ...(isQuiloLender && {
          phoneNumber: watcher[CoOwnerInputName.PhoneNumber]!,
          last4SSN: watcher[CoOwnerInputName.Last4Ssn],
        }),
      });

      dispatch(
        setCoOwner({
          email: watcher[CoOwnerInputName.Email],
          ...(isQuiloLender && {
            phoneNumber: watcher[CoOwnerInputName.PhoneNumber],
            last4SSN: watcher[CoOwnerInputName.Last4Ssn],
          }),
        }),
      );

      dispatch(
        setLoanPortal({
          isCoOwnerInformationComplete: true,
        }),
      );
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      dispatch(
        setLoadingState({
          type: `${LoadingType.RequiredInformationUpdating}${DashboardTabName.HomeOwnership}`,
          loading: false,
        }),
      );
    }
  });

  const coOwnerSubSectionState: SubSectionState = {
    isExist: isThereNonBorrowingCoOwner,
    isCompleted: coOwnerInformationSectionRolledUp.current!,
  };

  const documentsSubSectionState: SubSectionState = {
    isExist: isDocumentCategoryRequired(requiredDocuments, DocumentCategory.HomeVerification),
    isCompleted: isDocumentCategoryCompleted(requiredDocuments, DocumentCategory.HomeVerification),
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className={styles.requiredInformationContainer}>
          <Section
            title={'Homeownership Information'}
            subSectionStates={[
              {
                isExist: true,
                isCompleted: isHomeOwnershipInformationComplete,
              },
            ]}
          >
            <HomeOwnershipInfo />
          </Section>
          <FormProvider
            register={register}
            trigger={trigger}
            unregister={unregister}
            handleSubmit={handleSubmit}
            formState={formState}
            watch={watch}
            setValue={setValue}
            clearErrors={clearErrors}
            {...methods}
          >
            <form onSubmit={onSubmit}>
              <Section
                title={'Home Verification'}
                subSectionStates={[coOwnerSubSectionState, documentsSubSectionState]}
              >
                <SubSection state={coOwnerSubSectionState}>
                  <div>
                    <div className={styles.sectionHeader}>Non-Borrowing Co-Owner Information</div>
                    <div className={styles.sectionTitle}>
                      Information for the "non-borrowing co-owner" of the subject property is needed to verify their
                      identity and provide them with a UCC-1 acknowledgement form to sign prior to loan closing. Please
                      provide the following information for {coOwnerState.firstName} {coOwnerState.lastName}.
                    </div>
                    <div className={styles.rowContainer}>
                      <Input
                        label={CoOwnerInputLabel.FirstName}
                        className={styles.formInput}
                        value={watcher[CoOwnerInputName.FirstName]}
                        disabled
                      />
                      <Input
                        label={CoOwnerInputLabel.MiddleName}
                        className={styles.formInput}
                        value={watcher[CoOwnerInputName.MiddleName]}
                        disabled
                      />
                      <Input
                        label={CoOwnerInputLabel.LastName}
                        className={styles.formInput}
                        value={watcher[CoOwnerInputName.LastName]}
                        disabled
                      />
                    </div>
                    <div className={styles.rowContainer}>
                      <Input
                        label={CoOwnerInputLabel.Address}
                        className={styles.formInput}
                        value={watcher[CoOwnerInputName.Address]}
                        disabled
                      />
                    </div>
                    <div className={styles.rowContainer}>
                      <div className={styles.largeInputContainer}>
                        <Input
                          label={CoOwnerInputLabel.City}
                          className={styles.largeInputContainer}
                          value={watcher[CoOwnerInputName.City]}
                          disabled
                        />
                      </div>
                      <Input
                        label={CoOwnerInputLabel.State}
                        className={styles.formInput}
                        value={watcher[CoOwnerInputName.State]}
                        disabled
                      />
                      <Input
                        label={CoOwnerInputLabel.Zip}
                        className={styles.formInput}
                        value={watcher[CoOwnerInputName.Zip]}
                        disabled
                      />
                    </div>
                    <div className={styles.rowContainer}>
                      <Input
                        label={CoOwnerInputLabel.Email}
                        errorMessage={formState.errors[CoOwnerInputName.Email]?.message}
                        className={styles.formInput}
                        name={CoOwnerInputName.Email}
                        onChange={(event: any) => {
                          clearErrors([CoOwnerInputName.Email]);

                          setValue(CoOwnerInputName.Email, removeSpaces(event.target.value?.toLowerCase()));

                          if (formState.isSubmitted) {
                            trigger(CoOwnerInputName.Email);
                          }
                        }}
                        value={watcher[CoOwnerInputName.Email]}
                        disabled={isCoOwnerInformationComplete}
                      />
                      {isQuiloLender && (
                        <PhoneNumberInput
                          label={CoOwnerInputLabel.PhoneNumber}
                          errorMessage={formState.errors[CoOwnerInputName.PhoneNumber]?.message}
                          className={styles.formInput}
                          name={CoOwnerInputName.PhoneNumber}
                          onChange={(value: string) => {
                            clearErrors([CoOwnerInputName.PhoneNumber]);

                            setValue(CoOwnerInputName.PhoneNumber, value);

                            if (formState.isSubmitted) {
                              trigger(CoOwnerInputName.PhoneNumber);
                            }
                          }}
                          value={watcher[CoOwnerInputName.PhoneNumber]}
                          disabled={isCoOwnerInformationComplete}
                        />
                      )}
                    </div>
                    {isQuiloLender && (
                      <div className={styles.rowContainer}>
                        <Input
                          label={CoOwnerInputLabel.Last4Ssn}
                          className={styles.formInput}
                          name={CoOwnerInputName.Last4Ssn}
                          type="text"
                          maxLength={LAST_4_DIGITS_SSN_LENGTH}
                          onChange={(event) => {
                            const value = removeNonDigits(event.target.value.trim());

                            clearErrors(CoOwnerInputName.Last4Ssn);
                            setValue(CoOwnerInputName.Last4Ssn, value);

                            if (formState.isSubmitted) {
                              trigger(CoOwnerInputName.Last4Ssn);
                            }
                          }}
                          errorMessage={formState.errors[CoOwnerInputName.Last4Ssn]?.message}
                          value={watcher[CoOwnerInputName.Last4Ssn]}
                          inputMode="numeric"
                          disabled={isCoOwnerInformationComplete}
                        />
                      </div>
                    )}
                  </div>
                  {!isCoOwnerInformationComplete && (
                    <Button className={styles.submitButton} onClick={onSubmit}>
                      Save
                    </Button>
                  )}
                </SubSection>
                <SubSection state={documentsSubSectionState}>
                  <UploadDocumentsCategory
                    title={coOwnerSubSectionState.isExist ? 'Documents Upload' : ''}
                    documents={getDocumentsByCategory(requiredDocuments, DocumentCategory.HomeVerification)}
                    category={DocumentCategory.HomeVerification}
                  />
                </SubSection>
              </Section>
            </form>
          </FormProvider>
        </div>
      )}
    </>
  );
};

export default HomeOwnershipTab;
